/**
 * Type enums for Typedarray-backed buffers.
 *
 * {@link GLType}
 * {@link GL2TYPE}
 * {@link TYPE2GL}
 */
export var Type;
(function (Type) {
    Type[Type["U8"] = 0] = "U8";
    Type[Type["U8C"] = 1] = "U8C";
    Type[Type["I8"] = 2] = "I8";
    Type[Type["U16"] = 3] = "U16";
    Type[Type["I16"] = 4] = "I16";
    Type[Type["U32"] = 5] = "U32";
    Type[Type["I32"] = 6] = "I32";
    Type[Type["F32"] = 7] = "F32";
    Type[Type["F64"] = 8] = "F64";
})(Type || (Type = {}));
/**
 * WebGL numeric type constants. Use {@link GL2TYPE} to convert, if needed.
 *
 * {@link Type}
 * {@link GL2TYPE}
 * {@link TYPE2GL}
 */
export var GLType;
(function (GLType) {
    GLType[GLType["I8"] = 5120] = "I8";
    GLType[GLType["U8"] = 5121] = "U8";
    GLType[GLType["I16"] = 5122] = "I16";
    GLType[GLType["U16"] = 5123] = "U16";
    GLType[GLType["I32"] = 5124] = "I32";
    GLType[GLType["U32"] = 5125] = "U32";
    GLType[GLType["F32"] = 5126] = "F32";
})(GLType || (GLType = {}));
/**
 * Conversion from {@link GLType} to {@link Type} enums.
 */
export const GL2TYPE = {
    [GLType.I8]: Type.I8,
    [GLType.U8]: Type.U8,
    [GLType.I16]: Type.I16,
    [GLType.U16]: Type.U16,
    [GLType.I32]: Type.I32,
    [GLType.U32]: Type.U32,
    [GLType.F32]: Type.F32,
};
/**
 * Potentially lossy conversion from {@link Type} to {@link GLType} enums.
 *
 * Not all enums are mappable:
 *
 * - `F64` maps to `undefined`, since unsupported by WebGL
 * - `U8C` maps to U8
 */
export const TYPE2GL = {
    [Type.I8]: GLType.I8,
    [Type.U8]: GLType.U8,
    [Type.U8C]: GLType.U8,
    [Type.I16]: GLType.I16,
    [Type.U16]: GLType.U16,
    [Type.I32]: GLType.I32,
    [Type.I32]: GLType.I32,
    [Type.U32]: GLType.U32,
    [Type.F32]: GLType.F32,
    [Type.F64]: undefined,
};
/**
 * Size information (in bytes) for {@link Type} enums. For {@link GLType}, use this
 * form, e.g. `SIZEOF[GL2TYPE[GLType.F32]]`
 */
export const SIZEOF = {
    [Type.U8]: 1,
    [Type.U8C]: 1,
    [Type.I8]: 1,
    [Type.U16]: 2,
    [Type.I16]: 2,
    [Type.U32]: 4,
    [Type.I32]: 4,
    [Type.F32]: 4,
    [Type.F64]: 8,
};
export const TYPEDARRAY_CTORS = {
    [Type.U8]: Uint8Array,
    [Type.U8C]: Uint8ClampedArray,
    [Type.I8]: Int8Array,
    [Type.U16]: Uint16Array,
    [Type.I16]: Int16Array,
    [Type.U32]: Uint32Array,
    [Type.I32]: Int32Array,
    [Type.F32]: Float32Array,
    [Type.F64]: Float64Array,
    [GLType.U8]: Uint8Array,
    [GLType.I8]: Int8Array,
    [GLType.U16]: Uint16Array,
    [GLType.I16]: Int16Array,
    [GLType.U32]: Uint32Array,
    [GLType.I32]: Int32Array,
    [GLType.F32]: Float32Array,
};
export function typedArray(type, ...xs) {
    return new TYPEDARRAY_CTORS[type](...xs);
}
/**
 * Returns the smallest possible *unsigned* int type enum for given `x`.
 * E.g. if `x <= 256`, the function returns `Type.U8`.
 *
 * @param x - value to classify
 */
export const uintType = (x) => x <= 0x100 ? Type.U8 : x <= 0x10000 ? Type.U16 : Type.U32;
/**
 * Returns the smallest possible *signed* int type enum for given `x`.
 * E.g. if `x >= -128 && x < 128`, the function returns `Type.I8`.
 *
 * @param x - value to classify
 */
export const intType = (x) => x >= -0x80 && x < 0x80
    ? Type.I8
    : x >= -0x8000 && x < 0x8000
        ? Type.I16
        : Type.I32;
